// src/components/views/PaymentInformationView.js
import React from 'react';
import { ArrowLeft, CreditCard } from 'lucide-react';

const PaymentInformationView = ({ 
  selectedBusiness, 
  selectedLicense, 
  paymentData, 
  paymentDetails, 
  systems, 
  paymentLoading, 
  paymentByIdLoading, 
  paymentDetailsLoading, 
  paymentByIdError, 
  paymentDetailsError, 
  onBack 
}) => {
  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Format payment status
  const formatPaymentStatus = (status) => {
    if (status === "F" || status === "Paid") return "Paid";
    if (status === "P" || status === "Partial") return "Partial";
    if (status === "U" || status === "Unpaid") return "Unpaid";
    return status;
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    const formattedStatus = formatPaymentStatus(status);
    switch (formattedStatus) {
      case 'Paid': return 'bg-gradient-to-r from-green-50 to-green-100 text-green-800 border border-green-200';
      case 'Partial': return 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 border border-yellow-200';
      case 'Unpaid': return 'bg-gradient-to-r from-red-50 to-red-100 text-red-800 border border-red-200';
      default: return 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 border border-gray-200';
    }
  };

  if (!selectedLicense) {
    return (
      <div>
        <div className="mb-6">
          <button 
            onClick={onBack}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center space-x-2 transition-colors duration-200 font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back to Business Collection</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Information</h1>
          <p className="text-gray-600">No license selected</p>
        </div>
      </div>
    );
  }

  const isLoading = paymentLoading || paymentByIdLoading || paymentDetailsLoading;
  const hasError = paymentByIdError || paymentDetailsError;

  if (isLoading && !paymentData) {
    return (
      <div>
        <div className="mb-6">
          <button 
            onClick={onBack}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center space-x-2 transition-colors duration-200 font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back to Business Collection</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Information</h1>
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600 font-medium">Loading payment data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Extract payment data from API response
  const extractPaymentData = (data) => {
    if (!data) return null;
    
    let paymentData = null;
    
    if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet) && data.ResultSet.length > 0) {
        paymentData = data.ResultSet[0];
      } else if (Array.isArray(data) && data.length > 0) {
        paymentData = data[0];
      } else if (data.payment_id || data.PaymentID) {
        paymentData = data;
      } else if (data.ResultSet && typeof data.ResultSet === 'object') {
        paymentData = data.ResultSet;
      }
    }
    
    return paymentData;
  };

  const currentPaymentData = extractPaymentData(paymentData) || paymentData;
  
  if (!currentPaymentData && !isLoading) {
    return (
      <div>
        <div className="mb-6">
          <button 
            onClick={onBack}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center space-x-2 transition-colors duration-200 font-medium"
          >
            <ArrowLeft size={20} />
            <span>Back to Business Collection</span>
          </button>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Information</h1>
          <p className="text-gray-600">No payment data available for this license</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <button 
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center space-x-2 transition-colors duration-200 font-medium"
        >
          <ArrowLeft size={20} />
          <span>Back to Business Collection</span>
        </button>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Information</h1>
          <p className="text-gray-600 font-medium">View payment details and transaction history</p>
        </div>
      </div>

      {hasError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{paymentByIdError || paymentDetailsError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Business Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <label className="block text-sm font-medium text-gray-600 mb-1">Business ID</label>
            <p className="font-semibold text-gray-900 text-lg">{selectedBusiness?.business_id || 'Not available'}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <label className="block text-sm font-medium text-gray-600 mb-1">Business Name</label>
            <p className="font-semibold text-gray-900 text-lg">{selectedBusiness?.business_name || 'Not available'}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <label className="block text-sm font-medium text-gray-600 mb-1">License ID</label>
            <p className="font-semibold text-gray-900 text-lg">{selectedLicense.license_id || 'Not available'}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <label className="block text-sm font-medium text-gray-600 mb-1">System</label>
            <p className="font-semibold text-gray-900 text-lg">
              {systems.find(s => s.system_id === selectedLicense.system_id)?.system_name || 'Unknown System'}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Payment Master</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Payment ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">License ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Invoice No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Payment Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Total Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Paid Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="hover:bg-gray-50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {currentPaymentData.payment_id || currentPaymentData.PaymentID || 'Not available'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {currentPaymentData.license_id || currentPaymentData.LicenseID || selectedLicense.license_id || 'Not available'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                  {currentPaymentData.invoice_no || currentPaymentData.InvoiceNo || 'Not available'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {currentPaymentData.payment_plan || currentPaymentData.PaymentPlan || selectedLicense.subscription_type || 'Not available'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                  LKR {currentPaymentData.total_amount || currentPaymentData.TotalAmount || '0.00'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                  LKR {currentPaymentData.paid_amount || currentPaymentData.PaidAmount || '0.00'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                  LKR {currentPaymentData.balance_amount || currentPaymentData.BalanceAmount || '0.00'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-4 py-1.5 inline-flex text-xs leading-5 font-bold rounded-full ${
                    getStatusBadgeColor(currentPaymentData.status || currentPaymentData.Status || 'Unpaid')
                  }`}>
                    {formatPaymentStatus(currentPaymentData.status || currentPaymentData.Status || 'Unpaid')}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Payment Details</h2>
          <p className="text-sm text-gray-600 mt-1">Transaction history for this payment</p>
        </div>
        <div className="overflow-x-auto">
          {paymentDetails && paymentDetails.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Payment Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Payment Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Reference No</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">Remarks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentDetails.map((payment, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50 hover:bg-gray-100 transition-colors duration-150'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.payment_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {payment.payment_method}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {payment.reference_no}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                      LKR {payment.amount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payment.remarks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <CreditCard className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No payment details available</h3>
              <p className="text-gray-500">No transaction history found for this payment.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentInformationView;